import type { ReactNode, JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ListStateRow } from './ListStateRow';

export interface FetchErrorStateProps {
  /** The error copy — pass the surface's existing i18n message verbatim. */
  message: ReactNode;
  /**
   * Refetch callback (usually the RTK Query hook's `refetch`). Omit to render
   * the message without a Retry button.
   */
  onRetry?: () => void;
  /**
   * banner — destructive inline box below/above a list (default);
   * row — full-width table-body row (in-table error states, needs `colSpan`);
   * plain — bare icon + copy + button, for embedding in a caller's own
   * container (e.g. inside a Card).
   */
  variant?: 'banner' | 'row' | 'plain';
  /** Column count for the `row` variant. */
  colSpan?: number;
  /** Extra classes on the outer element (the spanning cell for `row`). */
  className?: string;
  /** Testid on the outer element (the spanning cell for `row`). */
  'data-testid'?: string;
  /** Testid for the Retry button; defaults to `<data-testid>-retry-button`. */
  retryTestId?: string;
}

/**
 * Shared fetch-error state with a Retry affordance. Callers keep
 * their own guard logic (`error && !isPermissionDenied(error)` etc.) and copy;
 * this only standardizes the rendering so every failed data load offers an
 * in-place refetch instead of forcing a full page reload — matching legacy
 * portal behavior (MessageBar + Retry on `main`).
 */
export function FetchErrorState({
  message,
  onRetry,
  variant = 'banner',
  colSpan,
  className,
  'data-testid': dataTestId,
  retryTestId,
}: FetchErrorStateProps): JSX.Element {
  const { t } = useTranslation('common');
  const retryId = retryTestId ?? (dataTestId ? `${dataTestId}-retry-button` : undefined);

  const content = (
    <span className="inline-flex flex-wrap items-center justify-center gap-2">
      <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
      <span>{message}</span>
      {onRetry && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onRetry}
          data-testid={retryId}
        >
          {t('state.retry', { defaultValue: 'Retry' })}
        </Button>
      )}
    </span>
  );

  if (variant === 'row') {
    return (
      <ListStateRow
        colSpan={colSpan ?? 1}
        className={cn('h-24 text-center text-sm text-destructive', className)}
        cellTestId={dataTestId}
      >
        {content}
      </ListStateRow>
    );
  }

  if (variant === 'plain') {
    return (
      <span className={cn('text-sm text-destructive', className)} data-testid={dataTestId}>
        {content}
      </span>
    );
  }

  return (
    <div
      className={cn(
        'rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive',
        className,
      )}
      data-testid={dataTestId}
    >
      {content}
    </div>
  );
}
